import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import { createAjv } from "@jsonforms/core";
import { FormControlLabel, Switch } from "@mui/material";
import { Breadcrumb, Button, Divider } from "pepsico-ds";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DeleteConfirmationModal from "../../components/common/DeleteConfirmationModal/DeleteConfirmationModal";
import ToastNotification from "../../components/common/toastNotification/ToastNotification";
import JsonFormsWrapper from "../../components/jsonForms/jsonFormsWrapper/JsonFormsWrapper";
import { useAppConfig } from "../../context/AppConfigContext";
import { GlobalConfigContext } from "../../context/GlobalConfigContext";
import { createKeywordGroup } from "../../graphql/mutations/createKeywordGroup";
import { updateKeywordGroup } from "../../graphql/mutations/updateKeywordGroup";
import { updateKeywordGroupsStatus } from "../../graphql/mutations/updateKeywordGroupsStatus";
import { listCampaignsByStoreGroups } from "../../graphql/queries/listCampaignsByStoreGroups";
import { listStores } from "../../graphql/queries/listStores";
import { useToast } from "../../hooks/useToast";
import {
  createStoreFormSchema,
  createStoreInfoUISchema,
  getStoreAddressUISchema,
  storeFormSchema,
  storeInfoUISchema,
} from "./storeFormSchema";
import { getStoreAddressFields, STATUS } from "./storeConstants";
import "./storeManagement.scss";
import { formatValidationErrors } from "./storeValidationErrors";

const OUTLET_TYPE = "OUTLET";

const ajv = createAjv({ useDefaults: true });
const validateStore = ajv.compile(storeFormSchema);
const validateCreateStore = ajv.compile(createStoreFormSchema);

const generateSlug = (name) =>
  (name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

const initialFormData = {
  name: "",
  slug: "",
  imageUrl: null,
  keywords: [],
  status: STATUS.ACTIVE,
  metadata: {
    store_id: "",
    name: "",
    retailer: "",
    address: "",
    postcode: "",
    city: "",
    country: "",
    state: "",
    district: "",
    google_maps_link: "",
    geolocation: {
      latitude: null,
      longitude: null,
    },
  },
};

export default function StoreDetailPage({ readOnly = false }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  const { toast, showToast, closeToast } = useToast();
  const { programMarketConfig } = useContext(GlobalConfigContext);
  const { appConfig } = useAppConfig();
  const isSyncMarket = useMemo(
    () => programMarketConfig?.store_sync_type === "SYNC",
    [programMarketConfig?.store_sync_type]
  );
  const addressFields = useMemo(() => getStoreAddressFields(appConfig), [appConfig]);
  const addressUISchema = useMemo(
    () => getStoreAddressUISchema(addressFields),
    [addressFields]
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showValidation, setShowValidation] = useState(false);

  const [formData, setFormData] = useState(initialFormData);
  const [isFormReady, setIsFormReady] = useState(!isEditMode);

  const [executeCreateKeyword] = useMutation(createKeywordGroup);
  const [executeUpdateKeyword] = useMutation(updateKeywordGroup);
  const [executeUpdateStatus] = useMutation(updateKeywordGroupsStatus);
  const [loadedStore, setLoadedStore] = useState(null);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const client = useApolloClient();

  const { data: storesData, loading: storesLoading } = useQuery(listStores, {
    fetchPolicy: "network-only",
    skip: !isEditMode,
  });

  useEffect(() => {
    if (!isEditMode) {
      setFormData(initialFormData);
      setIsFormReady(true);
      return;
    }
    setIsFormReady(false);
    if (storesData?.listKeywordGroups) {
      const store = storesData.listKeywordGroups.find((s) => s.id === id);
      if (store) {
        const meta =
          typeof store.metadata === "string"
            ? JSON.parse(store.metadata)
            : store.metadata || {};
        const loaded = {
          id: store.id,
          name: store.name || "",
          slug: store.slug || generateSlug(store.name || ""),
          imageUrl: store.imageUrl || null,
          categories: store.categories || [],
          keywords: store.keywords || [],
          status: store.status || STATUS.ACTIVE,
          metadata: {
            store_id: meta.store_id || "",
            name: meta.name || "",
            retailer: meta.retailer || "",
            address: meta.address || "",
            postcode: meta.postcode ? String(meta.postcode) : "",
            city: meta.city || "",
            country: meta.country || "",
            state: meta.state || "",
            district: meta.district || "",
            google_maps_link: meta.google_maps_link || "",
            geolocation: {
              latitude: meta.geolocation?.latitude ?? null,
              longitude: meta.geolocation?.longitude ?? null,
            },
          },
        };
        setFormData(loaded);
        setLoadedStore(loaded);
        setIsFormReady(true);
      }
    }
  }, [isEditMode, storesData, id]);

  const handleFormChange = useCallback((data) => {
    setFormData((prev) => {
      const updated = {
        ...prev,
        ...data,
        id: prev.id,
        metadata: {
          ...prev.metadata,
          ...(data.metadata || {}),
          geolocation: {
            ...prev.metadata?.geolocation,
            ...(data.metadata?.geolocation || {}),
          },
        },
      };
      const currentName = data.name !== undefined ? data.name : prev.name;
      updated.slug = generateSlug(currentName);
      if (data.name !== undefined) {
        updated.metadata.name = data.name;
      }
      return updated;
    });
  }, []);

  const handleCancel = () => {
    navigate("/store-management");
  };

  useEffect(() => {
    const nextKeywords = [formData.name?.trim(), formData.metadata?.retailer?.trim()]
      .filter(Boolean)
      .reduce((acc, value) => (acc.includes(value) ? acc : [...acc, value]), []);
    if (JSON.stringify(formData.keywords) === JSON.stringify(nextKeywords)) {
      return;
    }
    setFormData((prev) => ({ ...prev, keywords: nextKeywords }));
  }, [formData.name, formData.metadata?.retailer, formData.keywords]);

  const jsonFormsConfig = useMemo(() => ({ showValidation }), [showValidation]);

  const validateForm = () => {
    const validator = isEditMode ? validateStore : validateCreateStore;
    const isValid = validator(formData);
    if (!isValid) {
      setShowValidation(true);
      const errorMessages = formatValidationErrors(validator.errors || []);
      showToast(
        "error",
        "Validation Error",
        errorMessages.length > 0
          ? errorMessages.join(", ")
          : "Please fix the highlighted errors before saving"
      );
      return false;
    }
    return true;
  };

  const saveStore = async () => {
    setIsSubmitting(true);

    try {
      const statusChanged = isEditMode && formData.status !== loadedStore?.status;

      // In SYNC market edit mode, only status can change. Skip the full
      // update path entirely to avoid phantom diffs from auto-derived fields.
      if (isSyncMarket && isEditMode) {
        if (!statusChanged) {
          setIsSubmitting(false);
          return;
        }
        const { data } = await executeUpdateStatus({
          variables: {
            input: { keywordGroupIds: [formData.id], status: formData.status },
          },
        });
        const result = data?.updateKeywordGroupsStatus;
        if (!result?.success) {
          throw new Error(result?.message || "Could not update the store status");
        }
        showToast("success", "Store Updated", "Store has been updated successfully");
        setIsSubmitting(false);
        setTimeout(() => navigate("/store-management"), 1200);
        return;
      }

      const payload = {
        name: formData.name,
        slug: generateSlug(formData.name),
        imageUrl: formData.imageUrl || null,
        type: OUTLET_TYPE,
        ...(isEditMode ? { categories: formData.categories || null } : {}),
        ...(statusChanged || !isEditMode ? { status: formData.status } : {}),
        keywords:
          formData.keywords?.length > 0 ? formData.keywords : [formData.name],
        metadata: {
          store_id: formData.metadata.store_id,
          name: formData.metadata.name || formData.name,
          retailer: formData.metadata.retailer || null,
          address: formData.metadata.address,
          postcode: formData.metadata.postcode,
          city: formData.metadata.city,
          country: formData.metadata.country,
          state: formData.metadata.state || null,
          district: formData.metadata.district || null,
          google_maps_link: formData.metadata.google_maps_link || null,
          geolocation: {
            latitude: parseFloat(formData.metadata.geolocation.latitude),
            longitude: parseFloat(formData.metadata.geolocation.longitude),
          },
        },
      };

      if (isEditMode && formData.id) {
        await executeUpdateKeyword({
          variables: { id: formData.id, input: payload },
        });
        showToast("success", "Store Updated", "Store has been updated successfully");
      } else {
        await executeCreateKeyword({
          variables: { input: payload },
        });
        showToast("success", "Store Created", "Store has been created successfully");
      }

      setIsSubmitting(false);
      setTimeout(() => navigate("/store-management"), 1200);
    } catch (error) {
      setIsSubmitting(false);
      showToast(
        "error",
        "Error",
        error.message || "An error occurred while saving the store"
      );
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    // Only a real deactivation of a store that belongs to groups needs a live-campaign check.
    const mappedGroups = loadedStore?.categories || [];
    const deactivating =
      isEditMode &&
      loadedStore?.status !== STATUS.INACTIVE &&
      formData.status === STATUS.INACTIVE;

    if (deactivating && mappedGroups.length > 0) {
      setIsSubmitting(true);
      try {
        const { data } = await client.query({
          query: listCampaignsByStoreGroups,
          variables: { storeGroups: mappedGroups },
          fetchPolicy: "network-only",
        });
        if (data?.listCampaignsByStoreGroups?.length > 0) {
          setIsSubmitting(false);
          setShowDeactivateConfirm(true);
          return;
        }
      } catch (error) {
        setIsSubmitting(false);
        showToast(
          "error",
          "Error",
          error.message || "Could not check for live campaigns"
        );
        return;
      }
    }

    await saveStore();
  };

  const handleConfirmDeactivate = () => {
    setShowDeactivateConfirm(false);
    saveStore();
  };

  const breadcrumbs = [
    {
      label: "Home",
      href: "/",
    },
    {
      label: "Campaign Studio",
      href: "/campaigns/campaign-list",
    },
    { label: "Store Management", href: "/store-management" },
    {
      label: isEditMode
        ? formData.name || (readOnly ? "View Store" : "Edit Store")
        : "Add Store",
      href: "#",
    },
  ];

  const isReadOnly = readOnly || (isSyncMarket && isEditMode);

  return (
    <div className="container">
      <div className="wrap">
        <Breadcrumb breadcrumbs={breadcrumbs} activeIndex={1} />

        {isEditMode && (storesLoading || !isFormReady) ? (
          <div className="store-detail-page">
            <p>Loading store data...</p>
          </div>
        ) : (
          <div className="store-detail-page">
            <div className="page-header">
              <h2 className="page-title">
                {isEditMode ? formData.name || "Edit Store" : "Add Single Store"}
              </h2>
            </div>
            <Divider />
            <div className="store-detail-section">
              <h3 className="section-title">Store information</h3>
              <JsonFormsWrapper
                jsonschema={isEditMode ? storeFormSchema : createStoreFormSchema}
                uischema={isEditMode ? storeInfoUISchema : createStoreInfoUISchema}
                data={formData}
                onChange={handleFormChange}
                config={jsonFormsConfig}
                readonly={isReadOnly}
              />
            </div>

            <div className="store-detail-section store-address-section">
              <h3 className="section-title">Store Address</h3>
              <JsonFormsWrapper
                jsonschema={isEditMode ? storeFormSchema : createStoreFormSchema}
                uischema={addressUISchema}
                data={formData}
                onChange={handleFormChange}
                config={jsonFormsConfig}
                readonly={isReadOnly}
              />
            </div>
            <Divider />
            <div className="store-detail-actions">
              <Button
                text={readOnly ? "Back" : "Cancel"}
                variant="secondary"
                size="medium"
                onClick={handleCancel}
                disabled={isSubmitting}
              />
              <div className="display-flex align-items-center">
                <FormControlLabel
                  className="store-status-toggle"
                  control={
                    <Switch
                      checked={formData.status !== STATUS.INACTIVE}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          status: e.target.checked ? STATUS.ACTIVE : STATUS.INACTIVE,
                        }))
                      }
                      disabled={isSubmitting || readOnly}
                    />
                  }
                  label={formData.status === STATUS.INACTIVE ? "Inactive" : "Active"}
                />

                {!readOnly && (
                  <Button
                    text="Save"
                    variant="primary"
                    size="medium"
                    onClick={handleSave}
                    disabled={isSubmitting}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        <DeleteConfirmationModal
          isOpen={showDeactivateConfirm}
          title="Deactivate store?"
          message="This store is mapped to a live campaign. Are you sure to deactivate?"
          confirmText="Deactivate"
          onConfirm={handleConfirmDeactivate}
          onCancel={() => setShowDeactivateConfirm(false)}
        />

        <ToastNotification {...toast} handleClose={closeToast} />
      </div>
    </div>
  );
}
